import { CircularProgress, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import axios from "axios";
import LanguageContext from "components/LanguageContext";
import HelpAndShare from "components/LayoutFront/HelpAndShare";
import Wrapper from "components/LayoutFront/Wrapper";
import MessageContext from "components/MessageContext";
import categories from "components/templates/geolocalisationDsfr/geolocalisation.categories.enum.json";
import GeolocMairieHeaderDsfr from "components/templates/geolocalisationDsfr/GeolocMairieHeaderDsfr";
import GeolocPhotographeHeaderDsfr from "components/templates/geolocalisationDsfr/GeolocPhotographeHeaderDsfr";
import InfoGeolocDsfr from "components/templates/geolocalisationDsfr/InfoGeolocDsfr";
import SearchBarDsfr from "components/templates/geolocalisationDsfr/SearchBarDsfr";
import Block from "components/templatesComponents/Block";
import Icon from "components/templatesComponents/Icon";
import { buttonStyled } from "components/templatesComponents/StyledComponent";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import PropTypes from "prop-types";
import React, { createRef, isValidElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import scrollIntoView from "scroll-into-view-if-needed";
import { getTranslateIndex } from "utils/commonUtils";
import t from "utils/locales/translation.json";

const GeolocalisationDsfr = (props) => {
  const { page } = props;

  const { contents, template, title, shortDescription } = page;

  const { language } = useContext(LanguageContext);
  const { displayError } = useContext(MessageContext);

  const { bottomDynamicPart } = contents;
  let { source } = contents;
  let editSourceComponent = null;
  if (isValidElement(source)) {
    editSourceComponent = source;
    source = source.props.content?.value;
  }

  const radius = process.env.geoloc_radius || "30km";

  const [mapComponents, setMapComponents] = useState({});
  const [position, setPosition] = useState([46.7667, 2.45]); // Centre France metroplitaine
  const [cityPosition, setCityPosition] = useState("");
  const [geoPoints, setGeoPoints] = useState([]);
  const [department, setDepartment] = useState(null);
  const [geoPointsRefs, setGeoPointsRefs] = useState({});
  const [beforeSearch, setBeforeSearch] = useState(true);
  const [geoPointsRefsText, setGeoPointsRefsText] = useState({});
  const [load, setLoad] = useState(true);
  const [tabCategory, setTabCategory] = useState("all");
  const [switchValue, setSwitchValue] = useState(false);
  const [showMoreCards, setShowMoreCards] = useState(geoPoints.length > 6);
  const [activeCard, setActiveCard] = useState("");

  const handleTabCategoryChange = (newValue) => {
    setTabCategory(newValue);
  };

  const geoPointsToDisplay = useMemo(() => {
    if (geoPoints.length === 0) return [];
    if (department) {
      geoPoints.sort((a, b) => {
        const postalCodeA = a._source.others.postalCode || "";
        const postalCodeB = b._source.others.postalCode || "";
        return postalCodeA.localeCompare(postalCodeB);
      });
    }
    if (source === "photographe" && tabCategory && tabCategory !== "all") {
      return geoPoints.filter((geoPointItem) => {
        const { category = "photographe" } = geoPointItem._source || {};
        return category && category.toLowerCase() === tabCategory.toLowerCase();
      });
    }
    if (source === "mairie") {
      if (switchValue) {
        return geoPoints.filter((geoPointItem) => {
          const { category } = geoPointItem._source || {};
          return !category || category.toLowerCase() !== "passeport";
        });
      }
    }
    return geoPoints;
  }, [geoPoints, source, tabCategory, department, switchValue]);

  const firstsGeoPoints = useMemo(() => geoPointsToDisplay.slice(0, 6), [geoPointsToDisplay]);
  const restGeoPoints = useMemo(() => geoPointsToDisplay.slice(6), [geoPointsToDisplay]);

  const getLocationIcon = (cat) => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line global-require
      const L = require("leaflet");
      return L.icon({
        iconUrl: `/location-icon-${cat?.toLowerCase() === categories[source].specific ? "rouge" : "bleu"}.png`,
        shadowUrl: iconShadow,
        iconSize: [36, 36],
        iconAnchor: [16, 36],
        popupAnchor: [0, -40],
      });
    }
    return null;
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      // eslint-disable-next-line global-require
      require("leaflet/dist/leaflet.css");
      // eslint-disable-next-line global-require
      require("./geo.css");
      // eslint-disable-next-line global-require
      setMapComponents(require("react-leaflet"));
    }
  }, []);

  useEffect(() => {
    const refs = {};
    const refsText = {};
    geoPoints.forEach((geoPointItem) => {
      refs[geoPointItem._source.customId] = createRef();
      refsText[geoPointItem._source.customId] = createRef();
    });
    setGeoPointsRefs(refs);
    setGeoPointsRefsText(refsText);
  }, [geoPoints]);

  const getInfo = useCallback(
    (lat, long, size = 100) => {
      axios
        .post(`/geoloc`, {
          lat,
          long,
          source,
          distance: department ? "150km" : radius,
          from: 0,
          size,
        })
        .then(({ data }) => {
          if (data?.hits?.total?.value <= size) {
            if (department) {
              setGeoPoints(
                data?.hits?.hits?.filter((hit) =>
                  (hit?._source?.others?.postalCode || "").trim().startsWith(department.code),
                ),
              );
            } else {
              setGeoPoints(data?.hits?.hits || []);
            }
            setLoad(false);
          } else {
            getInfo(lat, long, data?.hits?.total?.value);
          }
          setShowMoreCards(false);
        });
    },
    [department, radius, source],
  );

  useEffect(() => {
    if (position && position.length > 0 && !beforeSearch) {
      setLoad(true);
      getInfo(position[0], position[1]);
    }
  }, [position, beforeSearch, getInfo]);

  const showLocation = (positionNav) => {
    const { latitude, longitude } = positionNav.coords;
    setBeforeSearch(false);
    setPosition([latitude, longitude]);
  };

  const errorHandler = (err) => {
    let message = "";
    switch (err.code) {
      case 1:
        message = "Vous n'avez pas accès à la géolocalisation";
        break;
      case 2:
        message = "Votre position est indisponible";
        break;
      case 3:
        message = "Le delai de la requête est dépassé";
        break;
      default:
        message = "Une erreur est survenue";
    }
    displayError(message);
  };

  // Obtenir la localisation depuis le naviagteur
  const getLocation = () => {
    if (navigator.geolocation) {
      // timeout at 60000 milliseconds (60 seconds)
      const options = { timeout: 60000 };
      navigator.geolocation.getCurrentPosition(showLocation, errorHandler, options);
      setGeoPoints([]);
      setCityPosition("votre position");
    } else {
      // eslint-disable-next-line no-alert
      alert(t[language].geolocalisation.browser_alert);
    }
  };

  const mapRef = createRef();

  const handleMouseOverGeoPoint = (point) => () => {
    const ref = geoPointsRefs[point._source.customId] && geoPointsRefs[point._source.customId].current;
    if (ref && typeof ref.openPopup === "function") {
      ref.openPopup();
    }
  };

  const handleClickPopup = (point) => () => {
    setShowMoreCards(true);
    const refText = geoPointsRefsText[point._source.customId].current;
    setTimeout(() => {
      scrollIntoView(refText, {
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      setActiveCard(point._id);
    }, 0);
  };

  const handlePopupClose = (point) => () => {
    const ref2 = geoPointsRefsText[point._source.customId].current;
    ref2.style.backgroundColor = "transparent";
  };

  geoPointsToDisplay.forEach((geoPointItem) => {
    // eslint-disable-next-line no-param-reassign
    geoPointItem.opening = [];
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeMonday);
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeTuesday);
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeWednesday);
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeThursday);
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeFriday);
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeSaturday);
    geoPointItem.opening.push(geoPointItem._source.others.openingTimeSunday);
  });

  const { MapContainer, Marker, Popup, TileLayer } = mapComponents;

  const invalidateMap = () => {
    setTimeout(() => {
      if (mapRef?.current) {
        mapRef.current.invalidateSize(false);
      }
      return null;
    }, 0);
    return null;
  };

  useEffect(() => {
    if (mapRef?.current) {
      // Accessibilité: enlever le focus sur la map pour éviter de naviguer dans les éléments de la map avec le clavier qui n'ont pas de sens pour les utilisateurs de lecteurs d'écran
      mapRef.current._container.setAttribute("tabindex", "-1");
      mapRef.current._container.setAttribute("aria-hidden", "true");
      if (!beforeSearch) {
        mapRef.current.flyTo({ lat: position[0], lng: position[1] }, 10.5);
      }
    }
  }, [mapRef, position, beforeSearch]);

  const fullAddressToDisplay = useCallback(
    (geopoint) => {
      const { numVoie, typeVoie, libelleVoie, address, address2, postalCode, city } = geopoint._source.others || {};
      const addressToDisplay =
        source && source === "siv"
          ? `${numVoie} ${typeVoie} ${libelleVoie}`
          : `${address || ""}${address2 ? ` ${address2}` : ""}`;
      return `${addressToDisplay} ${postalCode || ""} ${city || ""}`;
    },
    [source],
  );

  const distanceToDisplay = useCallback(
    (geoPoint) => {
      if (geoPoint.sort && geoPoint.sort.length > 0) {
        if (geoPoint.sort[0] === 0) {
          return t[language].geolocalisation.near;
        }
        const convertDistance = (language === "FR" ? 1 : 0.621371) * geoPoint.sort[0];
        return `${Number.parseFloat(convertDistance).toFixed(1)} ${t[language].geolocalisation.from} ${cityPosition}`;
      }
      return "";
    },
    [cityPosition, language],
  );

  return (
    <Wrapper>
      <Stack sx={{ px: { xs: 2, lg: 0 } }}>
        {!!editSourceComponent && <Block>{editSourceComponent}</Block>}
        <h1>{title}</h1>
        {shortDescription && <p className="fr-text--xl">{shortDescription}</p>}
      </Stack>
      <hr />
      <Box sx={{ px: { xs: 2, lg: 0 }, pt: 3, pb: 4, "& .fr-text--md": { mb: 0.5 }, "& .fr-text--xs": { mb: 1.5 } }}>
        <p className="fr-text--md">{t[language].geolocalisation.input_search_label}</p>
        <p className="fr-text--xs">{t[language].geolocalisation.input_search_sublabel}</p>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={3}>
          <SearchBarDsfr
            setPosition={setPosition}
            setCityPosition={setCityPosition}
            setGeoPoints={setGeoPoints}
            setDepartment={setDepartment}
            setBeforeSearch={setBeforeSearch}
            setShowMoreCards={setShowMoreCards}
          />
          <Box
            component="button"
            type="button"
            className="fr-btn fr-btn--secondary"
            aria-label={t[language].geolocalisation.icon_tooltip}
            onClick={getLocation}
            sx={buttonStyled}
          >
            <Icon iconDSFR="map-pin-user-line" title={t[language].geolocalisation.icon_tooltip} />
            <p className="fr-ml-1w">{t[language].geolocalisation.locate}</p>
          </Box>
        </Stack>
      </Box>
      {!beforeSearch && (
        <>
          <Stack
            spacing={3}
            sx={{ mx: { xs: 2, lg: 0 }, mb: 4, "& h6": { display: "flex", alignItems: "center", "& span": { mr: 2 } } }}
          >
            <h6 role="status">
              {load ? <CircularProgress size={20} /> : geoPointsToDisplay.length}{" "}
              {
                t[language].geolocalisation[department ? "department_results_message" : "city_results_message"][
                  getTranslateIndex(geoPointsToDisplay.length)
                ]
              }
              {department ? `« ${department.nom} »` : radius}
              {t[language].geolocalisation[department ? "department_results_message_2" : "city_results_message_2"]}
            </h6>
            {source === "mairie" && (
              <GeolocMairieHeaderDsfr switchValue={switchValue} handleSwitchValueChange={setSwitchValue} />
            )}
            {source === "photographe" && (
              <GeolocPhotographeHeaderDsfr
                tabCategory={tabCategory}
                handleTabCategoryChange={handleTabCategoryChange}
              />
            )}
          </Stack>
          <Box sx={{ px: { xs: 2, lg: 0 }, mb: showMoreCards ? 3 : 4 }}>
            <div id="mapAnchorStart" />
            {!!MapContainer && (
              <Box
                sx={{
                  "& .leaflet-container": { width: "100%", height: "565px", zIndex: 1 },
                  "& .leaflet-control > a": {
                    backgroundImage: "none",
                  },
                }}
              >
                <MapContainer
                  center={position}
                  zoom={10.5}
                  maxZoom={15}
                  ref={mapRef}
                  zoomControl
                  onDragEnd={invalidateMap()}
                >
                  <TileLayer
                    url={`${process.env.geoloc_url}/{z}/{x}/{y}.png`}
                    attribution={process.env.geoloc_licence}
                  />

                  {geoPointsToDisplay.map(
                    (geoPointItem) =>
                      geoPointItem._source &&
                      geoPointItem._source.location && (
                        <Marker
                          icon={getLocationIcon(geoPointItem._source.category)}
                          position={geoPointItem._source.location.split(",")}
                          ref={geoPointsRefs[geoPointItem._source.customId]}
                          eventHandlers={{
                            mouseover: handleMouseOverGeoPoint(geoPointItem),
                          }}
                          onPopupClose={handlePopupClose(geoPointItem)}
                          key={geoPointItem._source.id}
                        >
                          <Popup>
                            <h3 className="fr-h6 fr-mb-0">{geoPointItem._source.others.name || ""}</h3>
                            <p className="fr-text--sm">{fullAddressToDisplay(geoPointItem)}</p>
                            <Stack
                              direction="row"
                              spacing={2}
                              sx={{ alignItems: "center", justifyContent: department ? "flex-end" : "space-between" }}
                            >
                              {department ? null : (
                                <p className="fr-text--xs fr-my-0">{distanceToDisplay(geoPointItem)}</p>
                              )}
                              <button
                                type="button"
                                className="fr-link fr-link--xs"
                                onClick={handleClickPopup(geoPointItem)}
                              >
                                {t[language].geolocalisation.see_more_infos}
                              </button>
                            </Stack>
                          </Popup>
                        </Marker>
                      ),
                  )}
                </MapContainer>
              </Box>
            )}
            <Grid container spacing={2} sx={{ mt: 0, maxHeight: showMoreCards ? 850 : undefined, overflowX: "auto" }}>
              {firstsGeoPoints.map(
                (geoPointItem, index) =>
                  geoPointItem._source.others && (
                    <InfoGeolocDsfr
                      geoPointItem={geoPointItem}
                      distanceToDisplay={distanceToDisplay(geoPointItem)}
                      fullAddressToDisplay={fullAddressToDisplay(geoPointItem)}
                      source={source}
                      department={department}
                      ref={geoPointsRefsText[geoPointItem._source.customId]}
                      index={index}
                      activeCard={activeCard}
                      setActiveCard={setActiveCard}
                      key={`geopoints-${geoPointItem._id}`}
                    />
                  ),
              )}
              {!load && !showMoreCards && (
                <Box sx={{ textAlign: "center", width: "100%", mb: 1 }}>
                  <button
                    type="button"
                    aria-live="polite"
                    className="fr-btn fr-btn--secondary fr-btn--sm fr-mt-3w"
                    onClick={() => setShowMoreCards(!showMoreCards)}
                  >
                    {t[language].geolocalisation.show_more_results}
                  </button>
                </Box>
              )}
              {showMoreCards &&
                restGeoPoints.map(
                  (geoPointItem, index) =>
                    geoPointItem._source.others && (
                      <InfoGeolocDsfr
                        geoPointItem={geoPointItem}
                        distanceToDisplay={distanceToDisplay(geoPointItem)}
                        fullAddressToDisplay={fullAddressToDisplay(geoPointItem)}
                        source={source}
                        department={department}
                        ref={geoPointsRefsText[geoPointItem._source.customId]}
                        index={index}
                        activeCard={activeCard}
                        setActiveCard={setActiveCard}
                        key={`geopoints-${geoPointItem._id}`}
                      />
                    ),
                )}
            </Grid>
          </Box>
        </>
      )}
      {bottomDynamicPart}
      <HelpAndShare alwaysDisplayHelp template={template} mt={2} />
    </Wrapper>
  );
};

GeolocalisationDsfr.propTypes = {
  page: PropTypes.shape().isRequired,
};

export default GeolocalisationDsfr;
